import React, { useState, useMemo, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';

import Button from 'components/common/button';
import InformationIconStroke from 'components/common/icons/stroke/information-stroke';
import SearchInput from 'components/common/search_input';
import Spinner from 'scripts/presentation/components/common/spinner_two';
import Tooltip from 'components/common/tooltip';

const VALID_SORT_DIRECTIONS = ['asc', 'desc'];

const InteractiveTable = ({
  columns,
  controlComponents,
  data,
  enableSearch,
  enableSorting,
  initialSort,
  isLoading,
  reorderRows,
  rowsPerPage,
  onSortConfigUpdate,
  pageNumber,
  onPageNumberUpdate,
}) => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState(() => {
    const column = initialSort?.key && columns.find(c => c.accessor === initialSort.key);
    if (column) {
      const defaultSortDirection = column.sortDescendingFirst ? 'desc' : 'asc';
      return {
        key: initialSort.key,
        direction: VALID_SORT_DIRECTIONS.includes(initialSort.direction) ? initialSort.direction : defaultSortDirection,
      };
    }

    return { key: null, direction: 'asc' };
  });
  const [currentPage, setCurrentPage] = useState(pageNumber || 1);
  useEffect(() => {
    setCurrentPage(pageNumber || 1);
  }, [pageNumber]);

  const isInitialRender = useRef(true);
  useEffect(() => {
    if (!isInitialRender.current) {
      setSortConfig(sortConfig);
    }

    if (isInitialRender.current) {
      isInitialRender.current = false;
    }
  }, [sortConfig]);

  // Sorting logic (only if sorting is enabled)
  const sortedData = useMemo(() => {
    if (!enableSorting) {
      return data;
    }

    if (!sortConfig.key) {
      return reorderRows ? reorderRows(data) : data;
    }

    const sorted = [...data].sort((a, b) => {
      const column = columns.find(c => c.accessor === sortConfig.key);
      const getValue = item => column?.sortValue(item) ?? item[sortConfig.key];

      const aValue = getValue(a);
      const bValue = getValue(b);

      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return reorderRows ? reorderRows(sorted) : sorted;
  }, [enableSorting, sortConfig.key, sortConfig.direction, data, columns, reorderRows]);

  // Filtering logic (only if search is enabled)
  const filteredData = useMemo(() => {
    setCurrentPage(pageNumber || 1);
    if (!enableSearch || !searchTerm) return sortedData;
    return sortedData.filter(item =>
      columns.some(column => {
        const value = item[column.accessor];
        let searchValue = value;
        if (column.sortValue) {
          searchValue = column.sortValue(item);
        } else if (React.isValidElement(value)) {
          if (typeof value.props.children === 'string') {
            searchValue = value.props.children;
          } else if (value.props.value !== undefined) {
            searchValue = value.props.value;
          }
        }
        return searchValue?.toString().toLowerCase().includes(searchTerm.toLowerCase());
      })
    );
  }, [searchTerm, sortedData, columns, enableSearch, pageNumber]);

  // Pagination logic (only if pagination is enabled).
  const paginatedData = useMemo(() => {
    if (!rowsPerPage) return filteredData;
    const startIndex = (currentPage - 1) * rowsPerPage;
    return filteredData.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredData, currentPage, rowsPerPage]);

  const requestSort = key => {
    if (!enableSorting) return;

    const column = columns.find(c => c.accessor === key);
    let direction = 'asc';

    if (sortConfig.key === key) {
      direction = sortConfig.direction === 'asc' ? 'desc' : 'asc';
    } else {
      direction = column.sortDescendingFirst ? 'desc' : 'asc';
    }

    setSortConfig({ key, direction });
    if (onSortConfigUpdate) {
      onSortConfigUpdate({ key, direction });
    }
  };

  const totalPages = rowsPerPage ? Math.max(1, Math.ceil(filteredData.length / rowsPerPage)) : 1;

  const renderSortIcon = columnKey => {
    if (!enableSorting) return null;

    const isColumnSorted = sortConfig.key === columnKey;
    const classes = classnames('icon', {
      'ion-arrow-up-c': isColumnSorted && sortConfig.direction === 'asc',
      'ion-arrow-down-c': !isColumnSorted || sortConfig.direction === 'desc',
    });
    return (
      <SortIconWrapper className={isColumnSorted ? 'sorted' : ''}>
        <i className={classes} />
      </SortIconWrapper>
    );
  };

  const renderInfoIcon = infoText => {
    if (!infoText) return null;

    return (
      <InfoIconWrapper>
        <Tooltip message={infoText}>
          <InformationIconStroke width="12px" />
        </Tooltip>
      </InfoIconWrapper>
    );
  };

  return (
    <div>
      <ControlsWrapper>
        {enableSearch && (
          <SearchWrapper>
            <SearchInput
              data-aid="interactiveTable-searchInput"
              disabled={isLoading}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder="Search..."
              type="text"
              value={searchTerm}
            />
          </SearchWrapper>
        )}
        {controlComponents && <ControlComponentsWrapper>{controlComponents}</ControlComponentsWrapper>}
      </ControlsWrapper>

      <TableWrapper columns={columns}>
        <HeaderRow>
          {columns.map(column => (
            <HeaderCell
              align={column.align}
              data-aid="interactiveTable-columnHeader"
              key={column.accessor}
              maxWidth={column.maxWidth}
              minWidth={column.minWidth}
              onClick={() => requestSort(column.accessor)}
              padding={column.padding}
              width={column.width}
            >
              <THContent align={column.align}>
                {column.align === 'right' && (
                  <HeaderText align={column.align}>
                    {enableSorting && renderSortIcon(column.accessor)}
                    <p>{column.header}</p>
                    {renderInfoIcon(column.infoText)}
                  </HeaderText>
                )}
                {column.align !== 'right' && (
                  <HeaderText align={column.align}>
                    {renderInfoIcon(column.infoText)}
                    <p>{column.header}</p>
                    {enableSorting && renderSortIcon(column.accessor)}
                  </HeaderText>
                )}
              </THContent>
            </HeaderCell>
          ))}
        </HeaderRow>

        {!isLoading && (
          <RowsWrapper>
            {paginatedData.map((row, rowIndex) => {
              return (
                <Row data-aid="interactiveTable-row" key={rowIndex}>
                  {columns.map(column => {
                    const isClickable = column.isCellClickable ? column.isCellClickable(row) : false;
                    return (
                      <Cell
                        align={column.align}
                        className={isClickable ? 'clickable' : ''}
                        data-aid="interactiveTable-cell"
                        key={column.accessor}
                        maxWidth={column.maxWidth}
                        minWidth={column.minWidth}
                        onClick={() => isClickable && column.onCellClick(row)}
                        padding={column.padding}
                        width={column.width}
                      >
                        {row[column.accessor]}
                      </Cell>
                    );
                  })}
                </Row>
              );
            })}
          </RowsWrapper>
        )}
      </TableWrapper>

      {isLoading && (
        <SpinnerContainer data-aid={`interactiveTable-loadingSpinner`}>
          <Spinner size={48} stroke={3} />
        </SpinnerContainer>
      )}

      {/* Pagination Controls (Only if enabled). */}
      {!isLoading && rowsPerPage && (
        <Pagination>
          <Button
            data-aid="interactiveTable-previousButton"
            disabled={currentPage <= 1}
            onClick={() => {
              setCurrentPage(prev => Math.max(prev - 1, 1));
              if (onPageNumberUpdate) {
                onPageNumberUpdate(Math.max(pageNumber - 1, 1));
              }
            }}
          >
            {t('Previous')}
          </Button>
          <span data-aid="interactiveTable-paginationText">
            {t('Page {{currentPage}} of {{totalPages}}', { currentPage, totalPages })}
          </span>
          <Button
            data-aid="interactiveTable-nextButton"
            disabled={currentPage >= totalPages}
            onClick={() => {
              setCurrentPage(prev => Math.min(prev + 1, totalPages));
              if (onPageNumberUpdate) {
                onPageNumberUpdate(Math.min(pageNumber + 1, totalPages));
              }
            }}
          >
            {t('Next')}
          </Button>
        </Pagination>
      )}
    </div>
  );
};

InteractiveTable.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      header: PropTypes.string.isRequired,
      accessor: PropTypes.string.isRequired,
      align: PropTypes.oneOf(['left', 'right', 'center']),
      sortValue: PropTypes.func,
      infoText: PropTypes.string,
      minWidth: PropTypes.string,
      maxWidth: PropTypes.string,
      padding: PropTypes.string,
      width: PropTypes.string,
      sortDescendingFirst: PropTypes.bool,
      // takes a row as an argument and returns a boolean
      isCellClickable: PropTypes.func,
      // takes a row as an argument
      onCellClick: PropTypes.func,
    })
  ).isRequired,
  controlComponents: PropTypes.node,
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  enableSearch: PropTypes.bool,
  enableSorting: PropTypes.bool,
  enablePagination: PropTypes.bool,
  initialSort: PropTypes.shape({
    key: PropTypes.string.isRequired,
    direction: PropTypes.oneOf(['asc', 'desc']),
  }),
  isLoading: PropTypes.bool,
  onSortConfigUpdate: PropTypes.func,
  reorderRows: PropTypes.func,
  rowsPerPage: PropTypes.number,
  pageNumber: PropTypes.number,
  onPageNumberUpdate: PropTypes.func,
};

export const SortIconWrapper = styled.span`
  align-items: center;
  display: flex;
  flex-shrink: 0;
  opacity: 0;
  padding: ${p => p.theme.spacing.small};
  transition: opacity 0.2s ease;

  &.sorted {
    opacity: 1;
  }
`;

const TableWrapper = styled.div`
  border: none;
  padding: ${p => p.theme.spacing.medium};
  display: grid;
  grid-template-columns: ${p => p.columns.map(col => col.width || 'auto').join(' ')};
`;

const HeaderRow = styled.div`
  display: contents;
`;

const RowsWrapper = styled.div`
  display: contents;
  background-color: ${p => p.theme.colors.white};
`;

export const Row = styled.div`
  display: contents;

  &:hover > * {
    background-color: ${p => p.theme.colors.gray100};
    &:first-child {
      border-top-left-radius: ${p => p.theme.borderRadius.xsmall};
      border-bottom-left-radius: ${p => p.theme.borderRadius.xsmall};
    }
    &:last-child {
      border-top-right-radius: ${p => p.theme.borderRadius.xsmall};
      border-bottom-right-radius: ${p => p.theme.borderRadius.xsmall};
    }
  }
`;

const BaseCell = styled.div`
  padding: ${p => p.padding ?? p.theme.spacing.medium};
  text-align: ${p => p.align || 'left'};
  min-width: ${p => p.minWidth ?? 'auto'};
  max-width: ${p => p.maxWidth ?? 'auto'};
  overflow: hidden;
  text-overflow: ellipsis;
  background-color: ${p => p.theme.colors.white};
`;

export const HeaderCell = styled(BaseCell)`
  color: ${p => p.theme.colors.black};
  cursor: pointer;
  font-weight: bold;

  &:hover ${SortIconWrapper} {
    opacity: 1;
  }
`;

export const Cell = styled(BaseCell)`
  &.clickable {
    cursor: pointer;

    &:hover {
      color: ${p => p.theme.colors.green400};
    }
  }
`;

export const THContent = styled.div`
  align-items: center;
  display: flex;
  justify-content: ${p => (p.align === 'right' ? 'flex-end' : 'flex-start')};
  padding: ${p => p.theme.spacing.small};
  width: auto;
`;

export const HeaderText = styled.div`
  align-items: center;
  display: flex;
  gap: ${p => p.theme.spacing.xsmall};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const Pagination = styled.div`
  display: flex;
  justify-content: space-between;
  padding-bottom: ${p => p.theme.spacing.medium};
  padding-top: ${p => p.theme.spacing.medium};
`;

const SpinnerContainer = styled.div`
  align-items: center;
  display: flex;
  flex: 1 1 300px;
  justify-content: center;
  width: 100%;
`;

export const ControlsWrapper = styled.div`
  align-items: center;
  display: flex;
  gap: ${p => p.theme.spacing.inlineMedium};
  margin-bottom: ${p => p.theme.spacing.medium};
  width: 100%;
`;

const SearchWrapper = styled.div`
  flex-shrink: 0;
  width: 300px;
`;

const ControlComponentsWrapper = styled.div`
  align-items: left;
  display: flex;
  width: 100%;
`;

export const InfoIconWrapper = styled.span`
  padding: ${p => p.theme.spacing.small};
`;

export default InteractiveTable;
